import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../firebase';

interface MinimalistLoginPageProps {
  t: any;
  isRTL: boolean;
  onLoginSuccess: (user: any) => void;
}

const LoginContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  position: relative;
`;

const LoginCard = styled(motion.div)`
  background: white;
  border-radius: 20px;
  padding: 3rem;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 400px;
  position: relative;
`;

const LogoSection = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

const LogoContainer = styled.div`
  width: 100px;
  height: 100px;
  background: #dc2626;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem auto;
  overflow: hidden;
  padding: 12px;
`;

const Logo = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  filter: brightness(0) invert(1); /* Makes the logo white */
`;

const Title = styled.h1`
  font-size: 1.8rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 0.5rem;
`;

const Subtitle = styled.p`
  color: #6b7280;
  font-size: 0.95rem;
`;

const Form = styled.form`
  margin-top: 2rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;
`;

const InputWrapper = styled.div`
  position: relative;
`;

const Input = styled.input<{ $hasError?: boolean }>`
  width: 100%;
  padding: 0.875rem 3rem 0.875rem 1rem;
  border: 2px solid ${props => props.$hasError ? '#ef4444' : '#d1d5db'};
  border-radius: 10px;
  font-size: 1rem;
  background: #f9fafb;
  color: #1f2937;
  transition: all 0.2s ease;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: #dc2626;
    background: white;
    box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
  }
  
  &::placeholder {
    color: #9ca3af;
  }
`;

const IconWrapper = styled.div`
  position: absolute;
  top: 50%;
  right: 1rem;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    width: 100%;
    height: 100%;
    opacity: 0.6;
  }
`;

const PasswordToggle = styled.button`
  position: absolute;
  top: 50%;
  right: 1rem;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  
  img {
    width: 100%;
    height: 100%;
    opacity: 0.6;
    transition: opacity 0.2s ease;
  }
  
  &:hover img {
    opacity: 1;
  }
`;

const LoginButton = styled(motion.button)<{ $loading?: boolean }>`
  width: 100%;
  background: #dc2626;
  color: white;
  border: none;
  padding: 0.875rem 1.5rem;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 600;
  cursor: ${props => props.$loading ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  margin-top: 1rem;
  
  &:hover:not(:disabled) {
    background: #b91c1c;
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }
`;

const ErrorMessage = styled(motion.div)`
  background: #fef2f2;
  color: #dc2626;
  padding: 0.875rem 1rem;
  border-radius: 10px;
  margin-bottom: 1rem;
  font-size: 0.875rem;
  text-align: center;
  border: 1px solid #fecaca;
`;

const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const MinimalistLoginPage: React.FC<MinimalistLoginPageProps> = ({ t, isRTL, onLoginSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      onLoginSuccess(userCredential.user);
    } catch (error: any) {
      if (error.code === 'auth/invalid-credential' || error.code === 'auth/wrong-password' || error.code === 'auth/user-not-found') {
        setError(t.login.invalidCredentials);
      } else {
        setError(t.login.loginError);
      }
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <LoginContainer>
      <LoginCard
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <LogoSection>
          <LogoContainer>
            <Logo src="/images/logo192.png" alt={t.altTexts.logo} />
          </LogoContainer>
          <Title>{t.login.title}</Title>
          <Subtitle>{t.login.subtitle}</Subtitle>
        </LogoSection>

        <Form onSubmit={handleSubmit}>
          {error && (
            <ErrorMessage
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              {error}
            </ErrorMessage>
          )}

            <FormGroup>
            <Label>{t.login.emailLabel}</Label>
            <InputWrapper>
                <Input
                  $hasError={!!error}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                placeholder={t.login.emailPlaceholder}
                  required
              />
              <IconWrapper>
                <img 
                  src="/images/login/mail.svg" 
                  alt="Email" 
                  onError={(e) => {
                    console.error('Failed to load email icon:', e.currentTarget.src);
                    e.currentTarget.style.display = 'none';
                  }}
                  onLoad={(event) => console.log('Email icon loaded successfully:', event.currentTarget.src)}
                />
              </IconWrapper>
            </InputWrapper>
            </FormGroup>

            <FormGroup>
            <Label>{t.login.passwordLabel}</Label>
            <InputWrapper>
                <Input
                  $hasError={!!error}
                type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                placeholder={t.login.passwordPlaceholder}
                  required
              />
              <PasswordToggle
                type="button"
                onClick={togglePasswordVisibility}
                aria-label={showPassword ? t.accessibility.hidePassword : t.accessibility.showPassword}
              >
                <img 
                  src={showPassword ? "/images/login/hide.svg" : "/images/login/unhide.svg"}
                  alt={showPassword ? "Hide" : "Show"}
                  onError={(e) => {
                    console.error('Failed to load password icon');
                    e.currentTarget.style.display = 'none';
                  }}
                  onLoad={() => console.log('Password icon loaded')}
                />
              </PasswordToggle>
            </InputWrapper>
            </FormGroup>

            <LoginButton 
              $loading={loading} 
              type="submit" 
              disabled={loading}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {loading ? (
                <LoadingSpinner />
              ) : (
              t.login.loginButton
              )}
            </LoginButton>
        </Form>
      </LoginCard>
    </LoginContainer>
  );
};

export default MinimalistLoginPage;
